import React, { useState, useEffect } from "react";
import { lumi } from "../lib/lumi";
import {ShoppingCart, Plus, Minus, Trash2, Package, Lock, User, Upload, Edit, X, AlertTriangle} from 'lucide-react';
import toast from "react-hot-toast";

interface CartItem {
  id: string;
  name: string;
  price: number;
  size: string;
  quantity: number;
  image: string;
}

function Store() {
  const [user, setUser] = useState(lumi.auth.user);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [momoTransactionId, setMomoTransactionId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange(({ user }) => {
      setUser(user);
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const result = await lumi.entities.storeProducts.list({});
      setProducts(result.list);
    } catch (error) {
      console.error("Failed to load products:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: any, size: string) => {
    const existingItem = cart.find((item) => item.id === product._id && item.size === size);
    if (existingItem) {
      setCart(cart.map((item) =>
      item.id === product._id && item.size === size ?
      { ...item, quantity: item.quantity + 1 } :
      item
      ));
    } else {
      setCart([...cart, {
        id: product._id,
        name: product.name,
        price: product.price,
        size,
        quantity: 1,
        image: product.imageUrl
      }]);
    }
    toast.success(`${product.name} (${size}) added to cart!`);
  };

  const updateQuantity = (id: string, size: string, delta: number) => {
    setCart(cart.map((item) =>
    item.id === id && item.size === size ?
    { ...item, quantity: Math.max(1, item.quantity + delta) } :
    item
    ));
  };

  const removeFromCart = (id: string, size: string) => {
    setCart(cart.filter((item) => !(item.id === id && item.size === size)));
    toast.success("Item removed from cart");
  };

  const getTotalAmount = () => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error("Please sign in to place an order");
      return;
    }

    if (cart.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    if (!momoTransactionId.trim()) {
      toast.error("Please enter your Momo transaction ID");
      return;
    }

    setSubmitting(true);
    try {
      await lumi.entities.storeOrders.create({
        memberId: user.userId,
        memberEmail: user.email,
        memberName: user.userName,
        items: cart.map((item) => ({
          productId: item.id,
          productName: item.name,
          size: item.size,
          quantity: item.quantity,
          unitPrice: item.price,
          totalPrice: item.price * item.quantity
        })),
        totalAmount: getTotalAmount(),
        momoTransactionId: momoTransactionId.trim(),
        momoPhoneNumber: "0530661997",
        orderDate: new Date(),
        status: "pending",
        createdAt: new Date()
      });

      toast.success("Order placed successfully!");
      setCart([]);
      setMomoTransactionId("");
      setShowCart(false);
    } catch (error) {
      console.error("Failed to place order:", error);
      toast.error("Failed to place order. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignIn = async () => {
    try {
      await lumi.auth.signIn();
      toast.success("Successfully signed in!");
    } catch (error) {
      console.error("Sign in failed:", error);
      toast.error("Sign in failed. Please try again.");
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!confirm("Are you sure you want to delete this product?")) return;
    try {
      await lumi.entities.storeProducts.delete(productId);
      toast.success("Product deleted!");
      loadProducts();
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error("Failed to delete product.");
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Lock size={64} className="text-[#DC143C] mx-auto mb-6" />
            <h1 className="text-4xl font-bold mb-4">MOBA91 Store</h1>
            <p className="text-gray-400 text-lg">Members-only merchandise store</p>
          </div>

          <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Sign In Required</h2>
            <p className="text-gray-400 mb-8">
              Access exclusive MOBA91 merchandise. Sign in to start shopping.
            </p>

            <button
              onClick={handleSignIn}
              className="px-8 py-4 bg-[#DC143C] hover:bg-red-600 rounded-lg font-semibold transition-all transform hover:scale-105 flex items-center justify-center space-x-2 mx-auto">

              <User size={20} />
              <span>Sign In to Shop</span>
            </button>
          </div>
        </div>
      </div>);

  }

  if (loading) {
    return (
      <div className="min-h-screen py-16 bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#DC143C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading store...</p>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen py-16 bg-gradient-to-br from-black via-[#1A1A1A] to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-12">
          <div>
            <h1 className="text-4xl font-bold mb-2 text-[#B91C1C]">MOBA91 Store</h1>
            <p className="text-gray-400">Exclusive merchandise for members</p>
          </div>
          <div className="flex items-center space-x-4">
            {user?.userRole === "ADMIN" &&
            <button
              onClick={() => setShowAddProduct(true)}
              className="px-6 py-3 bg-white hover:bg-gray-100 border-2 border-gray-300 text-black rounded-lg font-semibold transition-all flex items-center space-x-2">

                <Plus size={20} />
                <span>Add Product</span>
              </button>
            }
            <button
              onClick={() => setShowCart(true)}
              className="relative px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all flex items-center space-x-2">

              <ShoppingCart size={20} />
              <span>Cart</span>
              {cart.length > 0 &&
              <span className="absolute -top-2 -right-2 w-6 h-6 bg-white text-black rounded-full flex items-center justify-center text-xs font-bold">
                  {cart.reduce((sum, item) => sum + item.quantity, 0)}
                </span>
              }
            </button>
          </div>
        </div>

        {/* Products Grid */}
        {products.length === 0 ?
        <div className="text-center py-20">
            <Package size={64} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600">No products available yet</p>
            {user?.userRole === "ADMIN" &&
          <button
            onClick={() => setShowAddProduct(true)}
            className="mt-4 px-6 py-3 bg-[#DC143C] hover:bg-red-600 text-white rounded-lg font-semibold transition-all">

                Add First Product
              </button>
          }
          </div> :

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product) =>
          <ProductCard
            key={product._id}
            product={product}
            addToCart={addToCart}
            isAdmin={user?.userRole === "ADMIN"}
            onDelete={handleDeleteProduct} />

          )}
          </div>
        }

        {/* Payment Instructions */}
        <div className="mt-16 bg-white border-2 border-gray-200 rounded-2xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-4 text-[#DC143C]">Payment Instructions</h2>
          <div className="space-y-3 text-gray-700">
            <p>1. Add items to your cart</p>
            <p>2. Send payment via MTN Mobile Money to: <span className="text-black font-bold">0530661997</span></p>
            <p>3. Enter your transaction ID at checkout</p>
            <p>4. Your order will be confirmed once payment is verified</p>
          </div>
        </div>
      </div>

      {/* Cart Modal */}
      {showCart &&
      <CartModal
        cart={cart}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        getTotalAmount={getTotalAmount}
        momoTransactionId={momoTransactionId}
        setMomoTransactionId={setMomoTransactionId}
        handleCheckout={handleCheckout}
        submitting={submitting}
        onClose={() => setShowCart(false)} />

      }

      {/* Add Product Modal */}
      {showAddProduct &&
      <AddProductModal
        onClose={() => setShowAddProduct(false)}
        onSuccess={() => {
          setShowAddProduct(false);
          loadProducts();
        }} />

      }
    </div>);

}

function ProductCard({ product, addToCart, isAdmin, onDelete }: any) {
  const [selectedSize, setSelectedSize] = useState(product.sizes?.[0] || "One Size");

  return (
    <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-[#DC143C] transition-all group relative shadow-lg">
      {isAdmin &&
      <button
        onClick={() => onDelete(product._id)}
        className="absolute top-2 right-2 z-10 p-2 bg-red-500/80 hover:bg-red-600 rounded-lg transition-all">

          <Trash2 size={16} />
        </button>
      }
      <div className="aspect-square overflow-hidden">
        <img
          src={product.imageUrl}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2">{product.name}</h3>
        <p className="text-gray-400 text-sm mb-4">{product.description}</p>
        <div className="text-2xl font-bold text-[#DC143C] mb-4">GH₵ {product.price}</div>
        
        {product.sizes && product.sizes.length > 0 &&
        <div className="mb-4">
            <label className="block text-sm text-gray-400 mb-2">Size:</label>
            <select
            value={selectedSize}
            onChange={(e) => setSelectedSize(e.target.value)}
            className="w-full px-3 py-2 bg-[#2A2A2A] border border-[#DC143C]/30 rounded text-white">

              {product.sizes.map((size: string) =>
            <option key={size} value={size}>{size}</option>
            )}
            </select>
          </div>
        }

        <button
          onClick={() => addToCart(product, selectedSize)}
          disabled={product.inStock === false}
          className="w-full px-4 py-3 bg-[#DC143C] hover:bg-red-600 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-semibold transition-all flex items-center justify-center space-x-2">

          <Plus size={18} />
          <span>{product.inStock === false ? "Out of Stock" : "Add to Cart"}</span>
        </button>
      </div>
    </div>);

}

function CartModal({ cart, updateQuantity, removeFromCart, getTotalAmount, momoTransactionId, setMomoTransactionId, handleCheckout, submitting, onClose }: any) {
  const isTransactionIdValid = momoTransactionId.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold flex items-center space-x-2">
              <ShoppingCart size={28} className="text-[#DC143C]" />
              <span>Your Cart</span>
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-all">

              ✕
            </button>
          </div>

          {cart.length === 0 ?
          <div className="text-center py-12">
              <Package size={64} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Your cart is empty</p>
            </div> :

          <>
              <div className="space-y-4 mb-6">
                {cart.map((item: CartItem) =>
              <div key={`${item.id}-${item.size}`} className="bg-[#1A1A1A] border border-[#DC143C]/20 rounded-lg p-4">
                    <div className="flex items-center space-x-4">
                      <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded" />
                      <div className="flex-1">
                        <h3 className="font-semibold">{item.name}</h3>
                        <p className="text-sm text-gray-400">Size: {item.size}</p>
                        <p className="text-[#DC143C] font-bold">GH₵ {item.price}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                      onClick={() => updateQuantity(item.id, item.size, -1)}
                      className="p-1 bg-[#2A2A2A] hover:bg-[#DC143C] rounded transition-all">

                          <Minus size={16} />
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button
                      onClick={() => updateQuantity(item.id, item.size, 1)}
                      className="p-1 bg-[#2A2A2A] hover:bg-[#DC143C] rounded transition-all">

                          <Plus size={16} />
                        </button>
                      </div>
                      <button
                    onClick={() => removeFromCart(item.id, item.size)}
                    className="p-2 hover:bg-red-500/20 rounded transition-all">

                        <Trash2 size={18} className="text-red-400" />
                      </button>
                    </div>
                  </div>
              )}
              </div>

              <div className="border-t border-[#DC143C]/20 pt-6 mb-6">
                <div className="flex justify-between items-center text-xl font-bold mb-6">
                  <span>Total:</span>
                  <span className="text-[#DC143C]">GH₵ {getTotalAmount()}</span>
                </div>

                <div className="bg-[#1A1A1A] border border-[#DC143C]/30 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-400 mb-2">
                    Send payment to MTN Momo: <span className="text-white font-bold">0530661997</span>
                  </p>
                  <input
                  type="text"
                  placeholder="Enter Momo Transaction ID"
                  value={momoTransactionId}
                  onChange={(e) => setMomoTransactionId(e.target.value)}
                  className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#DC143C]" />

                </div>

                {/* Disclaimer */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-4 flex items-start space-x-3">
                  <AlertTriangle size={20} className="text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-200">
                    <strong>Important:</strong> If the transaction ID provided is false or invalid, your order will be immediately cancelled. Please ensure you enter the correct Momo transaction ID.
                  </div>
                </div>

                <button
                onClick={handleCheckout}
                disabled={submitting || !isTransactionIdValid}
                className="w-full px-6 py-4 bg-[#DC143C] hover:bg-red-600 disabled:bg-[#2A2A2A] disabled:text-gray-500 disabled:cursor-not-allowed rounded-lg font-semibold transition-all">

                  {submitting ? "Processing..." : "Complete Order"}
                </button>
              </div>
            </>
          }
        </div>
      </div>
    </div>);

}

function AddProductModal({ onClose, onSuccess }: any) {
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    sizes: ["S", "M", "L", "XL", "XXL"],
    imageUrl: "",
    videoUrl: "",
    inStock: true
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const result = await lumi.tools.file.upload([files[0]]);
      if (result[0].fileUrl) {
        setFormData({ ...formData, imageUrl: result[0].fileUrl });
        toast.success("Image uploaded!");
      }
    } catch (error) {
      console.error("Failed to upload image:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const result = await lumi.tools.file.upload([files[0]]);
      if (result[0].fileUrl) {
        setFormData({ ...formData, videoUrl: result[0].fileUrl });
        toast.success("Video uploaded!");
      }
    } catch (error) {
      console.error("Failed to upload video:", error);
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price || !formData.imageUrl) {
      toast.error("Please fill in all required fields");
      return;
    }

    setUploading(true);
    try {
      await lumi.entities.storeProducts.create({
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        sizes: formData.sizes,
        imageUrl: formData.imageUrl,
        videoUrl: formData.videoUrl,
        inStock: formData.inStock,
        createdAt: new Date()
      });
      toast.success("Product added successfully!");
      onSuccess();
    } catch (error) {
      console.error("Failed to add product:", error);
      toast.error("Failed to add product");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0A0A0A] border border-[#DC143C]/30 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Add New Product</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X size={24} />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Product Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white"
                placeholder="e.g., MOBA91 Polo Shirt" />

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white"
                rows={3}
                placeholder="Product description" />

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Price (GH₵) *</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className="w-full px-4 py-3 bg-[#2A2A2A] border border-[#DC143C]/30 rounded-lg text-white"
                placeholder="120" />

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Product Image *</label>
              {formData.imageUrl ?
              <div className="relative">
                  <img src={formData.imageUrl} alt="Preview" className="w-full h-48 object-cover rounded-lg mb-2" />
                  <button
                  onClick={() => setFormData({ ...formData, imageUrl: "" })}
                  className="absolute top-2 right-2 p-2 bg-red-500 rounded-lg">

                    <X size={16} />
                  </button>
                </div> :

              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#DC143C]/30 rounded-lg cursor-pointer hover:border-[#DC143C] transition-all">
                  <Upload size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-400">Click to upload image</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              }
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Product Video (Optional)</label>
              {formData.videoUrl ?
              <div className="relative">
                  <video src={formData.videoUrl} controls className="w-full h-48 rounded-lg mb-2" />
                  <button
                  onClick={() => setFormData({ ...formData, videoUrl: "" })}
                  className="absolute top-2 right-2 p-2 bg-red-500 rounded-lg">

                    <X size={16} />
                  </button>
                </div> :

              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-[#DC143C]/30 rounded-lg cursor-pointer hover:border-[#DC143C] transition-all">
                  <Upload size={32} className="text-gray-400 mb-2" />
                  <span className="text-sm text-gray-400">Click to upload video</span>
                  <input type="file" accept="video/*" onChange={handleVideoUpload} className="hidden" />
                </label>
              }
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.inStock}
                onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                className="w-4 h-4" />

              <label className="text-sm text-gray-300">In Stock</label>
            </div>

            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="w-full px-6 py-4 bg-[#DC143C] hover:bg-red-600 disabled:bg-[#2A2A2A] rounded-lg font-semibold transition-all">

              {uploading ? "Uploading..." : "Add Product"}
            </button>
          </div>
        </div>
      </div>
    </div>);

}

export default Store;